import { authStore, refreshAccessToken } from '@/store/auth'

/**
 * Fetch an authenticated file and trigger a client-side save. Shared by
 * apiClient.downloadAttachment (utils/api.ts) and
 * hrApi.downloadEmployeeDocument (features/hr/api.ts) — extracted so the
 * auth-header/credentials sequence lives in one place (plan 022 code
 * review). Both source routes are `@tenant_required`, so a plain `<a href>`
 * navigation (cookie-only, no Bearer header) 401s; fetch authenticated,
 * then trigger the save.
 *
 * Uses raw `fetch` rather than `lib/http.ts:request()` because the response is
 * a binary body, not JSON — but it has to reproduce that module's 401 handling
 * (single-flight `refreshAccessToken()`, then one retry). Without it a download
 * is the only action on a long-open page that fails on an expired token, and it
 * fails as "Download failed", which reads as a broken file rather than a stale
 * session.
 */
export async function runAuthenticatedDownload(
  url: string,
  fileName: string,
  isRetry: boolean = false,
): Promise<void> {
  const token = authStore.getState().accessToken
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  })
  if (res.status === 401 && !isRetry) {
    const newToken = await refreshAccessToken()
    if (newToken) return runAuthenticatedDownload(url, fileName, true)
    authStore.getState().clear()
    throw new Error('Authentication expired')
  }
  if (!res.ok) throw new Error(`download_failed_${res.status}`)
  const blob = await res.blob()
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(objectUrl)
}
